import { LegStatus, LegType, QueryFilter } from '@api/Trinium/types';
import { DispatchFilterMap } from '@components/DataGrid/Dispatch/types';
import { dispatchFilterMaps } from '@components/Filters/dispatchFilterMaps';
import { FilterOperator, FilterValue, FilterValueType } from '@components/Filters/types';
import { dayjsNow } from '@/Infrastructure/Utilities/dayjs.type';

export type FilterMapKey = keyof DispatchFilterMap;

/** One selectable value for a filter whose underlying field holds a known set of codes. */
export interface FilterValueOption {
  /** The code stored in the saved query */
  value: string;
  label: string;
  /** Swatch shown beside the label, for a value the board expresses as a row color rather than as text */
  color?: string;
}

/**
 * Filters whose value is an ID that has to be searched for rather than typed. The set behind them is far too large
 * to list, and the ID is not what a dispatcher knows the record by — left as free text, a filter on a customer, a
 * driver or a yard gets the name typed into it and silently matches nothing.
 */
export type FilterLookup = 'customer' | 'driver' | 'location';

/** A filter the user can add, assembled from its dispatchFilterMaps entry. */
export interface FilterFieldOption {
  key: FilterMapKey;
  displayName: string;
  fieldType: FilterValueType;
  /** Present only when the underlying field holds a known set of codes, which turns the value input into a picker. */
  values?: FilterValueOption[];
  /** Present when the value must be chosen from an API search instead of a fixed list. */
  lookup?: FilterLookup;
  isDisabledAdding?: boolean;
  /** Whether the field can be filtered for holding nothing. False where the board always has a value to show. */
  canBeBlank: boolean;
}

export interface FilterOperatorOption {
  operator: FilterOperator;
  /** Reads as the middle of a sentence: "<Display Name> <phrase> <value>" */
  phrase: string;
  /**
   * Matches the legs the field holds nothing on — "Driver is blank" is how a leg with no driver assigned gets found.
   * Saved as its `operator` with no value at all, which is the only thing the API takes to mean absence.
   */
  isBlank?: boolean;
}

/** The value a picker's synthetic blank entry carries. Empty is safe: no real code, ID or option value is empty. */
export const blankPickerValue = '';

/** What the blank entry reads as inside a value picker, parenthesized so it cannot be mistaken for a real record. */
export const blankPickerLabel = '(Blank)';

/** The one operator choice that saves without a value. Its phrase is also the wording an applied blank filter reads under. */
export const blankOperatorOption: FilterOperatorOption = { operator: '=', phrase: 'is blank', isBlank: true };

/** A filter the user built by choosing blank, whether the API omitted the value or returned it as null. */
export function isBlankFilterValue(value: FilterValue): boolean {
  return value === undefined || value === null;
}

function enumValueOptions(enumDefinition: Record<string, string>): FilterValueOption[] {
  return Object.entries(enumDefinition)
    .map(([code, name]) => ({ value: code, label: `${name} (${code})` }))
    .sort((left, right) => left.label.localeCompare(right.label, 'en', { sensitivity: 'base' }));
}

/**
 * Leg Priority is stored as a number that says nothing on its own — on the board it is only ever seen as the color it
 * paints the row, and it is set by clicking that color in the row actions. So it is picked here by the same color,
 * listed in the order those swatches sit in. The backgrounds are the ones the board cells use (RenderCell.module.css),
 * and 2 is the value a leg carries when no priority has been set, which the board leaves untinted.
 */
const legPriorityOptions: FilterValueOption[] = [
  { value: '2', label: 'None (White)', color: 'transparent' },
  { value: '1', label: 'Red', color: 'rgba(var(--mui-palette-priority2-main), 0.4)' },
  { value: '0', label: 'Yellow', color: 'rgba(var(--mui-palette-priority3-main), 0.4)' },
  { value: '-1', label: 'Green', color: 'rgba(var(--mui-palette-priority4-main), 0.4)' },
  { value: '-2', label: 'Orange', color: 'rgba(var(--mui-palette-priority5-main), 0.4)' }
];

/**
 * Filters whose DispatchLeg field holds a known set of values rather than free text. Job Category is also a closed
 * set but its values are per-terminal, so the editor merges those in once the categories have been fetched.
 */
const closedValueSets: Partial<Record<FilterMapKey, FilterValueOption[]>> = {
  LegPriority: legPriorityOptions,
  LegStatus: enumValueOptions(LegStatus),
  LegType: enumValueOptions(LegType),
  // getJobCatTransTypeString expands the API's single character into one of these
  TransportType: ['Export', 'Import', 'Local'].map((value) => ({ value, label: value }))
};

/** Which search backs each lookup filter. Pickup or Delivery covers both puId and delvId, so one location serves it. */
const lookupFilters: Partial<Record<FilterMapKey, FilterLookup>> = {
  Customer: 'customer',
  Delivery: 'location',
  Driver: 'driver',
  Pickup: 'location',
  PickupDelivery: 'location'
};

/**
 * Fields blank is not offered on. Leg # and Leg Status are required in the API response, and Leg Priority is
 * defaulted by the transform, so blank on any of the three is a filter that matches nothing.
 */
const neverBlankFields: FilterMapKey[] = ['Leg', 'LegPriority', 'LegStatus', 'PickupDelivery', 'ScheduledPickupDeliveryDate'];

/** Every filter a query can pick up, ordered by the name the user selects it under. */
export const filterFieldOptions: FilterFieldOption[] = (Object.keys(dispatchFilterMaps) as FilterMapKey[])
  .map((key) => ({
    key,
    displayName: dispatchFilterMaps[key].displayName,
    fieldType: dispatchFilterMaps[key].fieldType,
    values: closedValueSets[key],
    lookup: lookupFilters[key],
    isDisabledAdding: dispatchFilterMaps[key].isDisabledAdding,
    // A boolean is never blank to a dispatcher: the field not being set is what the No option already means
    canBeBlank: dispatchFilterMaps[key].fieldType !== 'boolean' && !neverBlankFields.includes(key)
  }))
  .sort((left, right) => left.displayName.localeCompare(right.displayName, 'en', { sensitivity: 'base' }));

/**
 * Operators offered per field type, in the order listed. '=' leads each list because it is the sane default for a
 * newly picked field. The phrase doubles as the wording an applied filter reads under, so the two cannot drift.
 */
const operatorsByFieldType: Record<FilterValueType, FilterOperatorOption[]> = {
  string: [
    { operator: '=', phrase: 'is' },
    { operator: 'startsWith', phrase: 'starts with' },
    { operator: 'in', phrase: 'is one of' },
    { operator: 'notIn', phrase: 'is not one of' }
  ],
  number: [
    { operator: '=', phrase: 'is' },
    { operator: '>', phrase: 'is greater than' },
    { operator: '>=', phrase: 'is at least' },
    { operator: '<', phrase: 'is less than' },
    { operator: '<=', phrase: 'is at most' },
    { operator: 'in', phrase: 'is one of' },
    { operator: 'notIn', phrase: 'is not one of' }
  ],
  date: [
    { operator: '=', phrase: 'is on' },
    { operator: '>', phrase: 'is after' },
    { operator: '>=', phrase: 'is on or after' },
    { operator: '<', phrase: 'is before' },
    { operator: '<=', phrase: 'is on or before' }
  ],
  boolean: [{ operator: '=', phrase: 'is' }]
};

/** The operators that still read as a sentence once the value is chosen from a picker rather than typed. */
const pickerOperators: FilterOperator[] = ['=', 'in', 'notIn'];

/**
 * "Starts with" cannot be aimed at a picker of whole codes, and neither can the ordering comparisons a number field
 * otherwise offers — "Leg Priority is greater than Yellow" is not something a dispatcher can reason about. A saved
 * query that already holds one of those still runs and still reads; it just cannot be built here any more.
 */
export function getOperatorOptions(field: FilterFieldOption): FilterOperatorOption[] {
  const operators = operatorsByFieldType[field.fieldType];
  const offered = field.values || field.lookup ? operators.filter(({ operator }) => pickerOperators.includes(operator)) : operators;

  if (!field.canBeBlank) return offered;

  // Directly behind '=', which every list leads with and which blank saves as, so the two equality choices read together
  return [offered[0], blankOperatorOption, ...offered.slice(1)];
}

/** The equality choice that still takes a value, which is where picking a real value out of a blank filter lands. */
export function getValueOperatorPhrase(operatorOptions: FilterOperatorOption[]): string {
  return operatorOptions.find(({ operator, isBlank }) => operator === '=' && !isBlank)?.phrase ?? '';
}

/** The collection operators compare against a list of values rather than a single one. */
export function isMultiValueOperator(operator: FilterOperator): boolean {
  return operator === 'in' || operator === 'notIn';
}

/**
 * A saved date filter holds a day offset from today rather than a calendar date, so the query keeps meaning the same
 * thing tomorrow. The resolved date comes along for the ride so the offset can be sanity checked while editing.
 */
export function formatDayOffset(offset: number): string {
  const resolved = dayjsNow().add(offset, 'd').format('MMM D, YYYY');
  if (offset === 0) return `today (${resolved})`;

  const magnitude = Math.abs(offset);

  return `${offset > 0 ? '+' : '-'}${String(magnitude)} ${magnitude === 1 ? 'day' : 'days'} (${resolved})`;
}

function describeFilterValue(field: FilterFieldOption, value: FilterValue): string {
  if (field.fieldType === 'boolean') return value ? 'Yes' : 'No';
  if (field.fieldType === 'date') return typeof value === 'number' ? formatDayOffset(value) : String(value);

  const label = (entry: string | number) => field.values?.find((option) => option.value === String(entry))?.label ?? String(entry);

  return Array.isArray(value) ? value.map(label).join(', ') : label(String(value ?? ''));
}

/**
 * States an applied filter as a sentence. Takes the field options rather than reading dispatchFilterMaps directly so
 * that the per-terminal Job Category labels resolve here too.
 */
export function describeFilter(filter: QueryFilter, fields: FilterFieldOption[]): string {
  const field = fields.find((candidate) => candidate.key === filter.map);
  // A saved query can name a filter this build no longer maps, the same way it can name a column the board discards
  const name = field?.displayName ?? filter.map;

  // Blank reads the same for every field type, so it needs neither the operator's phrase nor a value on the end
  if (isBlankFilterValue(filter.value)) return `${name} ${blankOperatorOption.phrase}`;

  if (!field) return `${name} ${filter.operator} ${String(filter.value)}`;

  const phrase = operatorsByFieldType[field.fieldType].find(({ operator }) => operator === filter.operator)?.phrase ?? filter.operator;

  return `${name} ${phrase} ${describeFilterValue(field, filter.value)}`;
}

/** Two filters on the same field are what express a range, so only an exact repeat is a duplicate. */
export function isSameFilter(left: QueryFilter, right: QueryFilter): boolean {
  if (left.map !== right.map || left.operator !== right.operator) return false;

  // JSON.stringify does not agree that an omitted value and an explicit null are the same blank filter
  if (isBlankFilterValue(left.value) || isBlankFilterValue(right.value)) {
    return isBlankFilterValue(left.value) && isBlankFilterValue(right.value);
  }

  return JSON.stringify(left.value) === JSON.stringify(right.value);
}
